import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  Close as CloseIcon,
  DeleteTwoTone as DeleteIcon,
  AddCircleTwoTone as AddIcon,
  LaunchTwoTone as LaunchIcon,
  SettingsTwoTone as SettingsIcon,
} from "@mui/icons-material";
import { ROUTES } from "@/routes/config";
import DataTable, { type Column } from "@/components/DataTable";
import { usePortals } from "./hooks/usePortals";
import type { PortalListItem } from "@/types";

const statusColor = (
  status: string,
): "success" | "info" | "warning" | "error" | "default" => {
  switch (status) {
    case "active":
      return "success";
    case "provisioning":
      return "info";
    case "pending":
      return "warning";
    case "inactive":
      return "default";
    case "suspended":
      return "error";
    default:
      return "default";
  }
};

const Portals: React.FC = () => {
  const navigate = useNavigate();
  const { portals, isLoading, deletePortal, isDeleting } = usePortals();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const handleDelete = (portal: PortalListItem) => {
    deletePortal(portal.id);
    setConfirmingId(null);
  };

  const columns: Column<PortalListItem>[] = [
    {
      id: "name",
      label: "Name",
      render: (row) => (
        <Typography variant="body2" fontWeight={700}>
          {row.name}
        </Typography>
      ),
    },
    {
      id: "slug",
      label: "Slug",
      render: (row) => (
        <Typography
          variant="body2"
          sx={{
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.8125rem",
          }}
        >
          {row.slug}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (row) => (
        <Chip
          label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
          color={statusColor(row.status)}
          size="small"
        />
      ),
    },
    {
      id: "portalAdminLink",
      label: "Portal Admin",
      align: "center",
      render: (row) => {
        if (row.status !== "active") {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Tooltip title="Open Portal Admin">
            <IconButton
              component="a"
              href={`https://${row.slug}.${import.meta.env.VITE_PORTAL_ADMIN_DOMAIN}`}
              target="_blank"
              rel="noreferrer"
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: "primary.main" }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      id: "agentPortalLink",
      label: "Agent Portal",
      align: "center",
      render: (row) => {
        if (row.status !== "active") {
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        }
        return (
          <Tooltip title="Open Agent Portal">
            <IconButton
              component="a"
              href={`https://${row.slug}.${import.meta.env.VITE_CUSTOMER_PORTAL_DOMAIN}`}
              target="_blank"
              rel="noreferrer"
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: "secondary.main" }}
            >
              <LaunchIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      id: "created",
      label: "Created",
      render: (row) => (
        <Typography variant="body2">
          {new Date(row.created_at).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      id: "actions",
      label: "Actions",
      align: "right",
      render: (row) => {
        if (confirmingId === row.id) {
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              justifyContent="flex-end"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 0.5 }}
              >
                Delete?
              </Typography>
              <Tooltip title="Confirm delete">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(row)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel">
                <IconButton
                  size="small"
                  onClick={() => setConfirmingId(null)}
                  disabled={isDeleting}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        }
        return (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setConfirmingId(row.id)}
                disabled={isDeleting}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Manage">
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  navigate(`/dashboard/portals/${row.id}`, {
                    state: { portal: row },
                  })
                }
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Portals
          </Typography>
          <Typography color="text.secondary">
            Create and manage all related portals in one place.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(ROUTES.CREATE_PORTAL)}
        >
          Create Portal
        </Button>
      </Stack>

      <Box sx={{ mt: 3 }}>
        <DataTable
          columns={columns}
          data={portals}
          keyExtractor={(row) => row.id}
          loading={isLoading}
          emptyMessage="No portals found"
          emptyDescription="Create your first portal to get started."
        />
      </Box>
    </Box>
  );
};

export default Portals;
